import * as THREE from 'three';
import { markInteractive, type PickInfo } from './interactive';
import { pickButton, type PanelButton } from './button';
import {
  contentBounds,
  onAssetsReady,
  paintPageButton,
  paintPagePanelFrame,
  paintPageTitleRow,
  treatiseBodyFont,
} from './style';
import { PANEL_PIXELS_PER_METER, UI_INK_MUTED } from '../config/constants';
import { playUiClick } from '../feedback/audioManager';

export type PanelOptions = {
  widthM: number;
  heightM: number;
  pixelsPerMeter?: number;
  /** Segno watermark opacity override (e.g. stronger on the colpo-picker step). */
  filigranaAlpha?: number;
};

/**
 * World-locked 3D panel styled as a printed treatise page: canvas-texture
 * plane with optional clickable buttons. Subclasses paint extra content via
 * getContext().
 */
export class Panel {
  readonly mesh: THREE.Mesh;
  readonly canvas: HTMLCanvasElement;
  protected texture: THREE.CanvasTexture;
  private buttons: PanelButton[] = [];
  private unsubscribeAssets: (() => void) | null = null;
  private lastRepaint: (() => void) | null = null;
  private readonly filigranaAlpha: number | undefined;

  constructor(opts: PanelOptions) {
    const ppm = opts.pixelsPerMeter ?? PANEL_PIXELS_PER_METER;
    this.filigranaAlpha = opts.filigranaAlpha;
    this.canvas = document.createElement('canvas');
    this.canvas.width = Math.round(opts.widthM * ppm);
    this.canvas.height = Math.round(opts.heightM * ppm);
    this.texture = new THREE.CanvasTexture(this.canvas);

    const mat = new THREE.MeshBasicMaterial({ map: this.texture, transparent: true });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(opts.widthM, opts.heightM), mat);
    markInteractive(this.mesh, this.canvas.width, this.canvas.height, (info) => this.onPick(info));

    // Repaint once textures + fonts land.
    this.unsubscribeAssets = onAssetsReady(() => this.lastRepaint?.());
  }

  getContext(): CanvasRenderingContext2D {
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    return ctx;
  }

  markDirty(): void {
    this.texture.needsUpdate = true;
  }

  setButtons(buttons: PanelButton[]): void {
    this.buttons = buttons;
  }

  /** Paint the page chrome only (no title/content). For custom layouts. */
  renderFrame(): void {
    const c = this.getContext();
    paintPagePanelFrame(c, this.canvas.width, this.canvas.height, {
      ...(this.filigranaAlpha !== undefined ? { filigranaAlpha: this.filigranaAlpha } : {}),
    });
    this.markDirty();
  }

  /** Default render: page + Cinzel title + optional subtitle + buttons. */
  render(title: string, subtitle?: string): void {
    this.lastRepaint = () => this.render(title, subtitle);
    const c = this.getContext();
    const w = this.canvas.width;
    const h = this.canvas.height;

    paintPagePanelFrame(c, w, h, {
      ...(this.filigranaAlpha !== undefined ? { filigranaAlpha: this.filigranaAlpha } : {}),
    });
    paintPageTitleRow(c, title, w, h);

    if (subtitle) {
      const b = contentBounds(w, h);
      c.save();
      c.font = treatiseBodyFont(h * 0.05, 400);
      c.fillStyle = UI_INK_MUTED;
      c.textAlign = 'center';
      c.textBaseline = 'top';
      c.fillText(subtitle, w / 2, b.y + h * 0.2, b.w);
      c.restore();
    }

    this.paintButtons(c);
    this.markDirty();
  }

  paintButtons(c: CanvasRenderingContext2D): void {
    for (const b of this.buttons) paintPageButton(c, b, b.variant ?? 'primary');
  }

  private onPick(info: PickInfo): void {
    const hit = pickButton(this.buttons, info.canvasX, info.canvasY);
    if (!hit) return;
    playUiClick();
    hit.onClick();
  }

  dispose(): void {
    this.unsubscribeAssets?.();
    this.unsubscribeAssets = null;
    this.lastRepaint = null;
    this.texture.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.geometry.dispose();
  }
}
